import { useAdjustMonthlyPaymentCapsMutation } from '@/services/monthlyPaymentCaps';
import { Box, Button, Heading, Modal, RadioGroup, toast } from '@palmetto/palmetto-components';
import { FormikValues, useFormik } from 'formik';
import { DateTime } from 'luxon';
import { SyntheticEvent } from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';

import { AdjustMonthlyPaymentCapTypes } from 'types';

export const AdjustMonthlyPaymentCap = ({
  isModalOpen,
  closeModal,
  currentMonthlyPaymentCap,
}: {
  isModalOpen: boolean;
  closeModal: (event?: SyntheticEvent) => void;
  currentMonthlyPaymentCap: {
    greaterThanZeroEscalationRate: string;
    zeroEscalationRate: string;
    orgMaxZeroEscalationRate?: string;
    orgMaxGreaterThanZeroEscalationRate?: string;
    capTableVersionUsed: string;
    dateCapsApplied: string;
    override: boolean;
    overrideDate: string;
    overrideUserName: string;
  };
}) => {
  const { id = '' } = useParams<{ id: string }>();
  const [adjustMonthlyPaymentCapForAccount] = useAdjustMonthlyPaymentCapsMutation();
  const formik = useFormik({
    initialValues: { adjustmentOption: AdjustMonthlyPaymentCapTypes.updatePaymentCaps },
    validationSchema: yup.object({
      adjustmentOption: yup.string().required('Please select an option'),
    }),
    enableReinitialize: true,
    onSubmit: async (values: FormikValues, { setSubmitting, resetForm }) => {
      try {
        await adjustMonthlyPaymentCapForAccount({
          id,
          data: { adjustmentOption: values.adjustmentOption },
        }).unwrap();
        toast.success('Monthly Payment Cap updated successfully');
        resetForm();
        closeModal();
      } catch (e) {
        console.error(e);
        toast.error('Error updating Monthly Payment Cap');
      } finally {
        setSubmitting(false);
      }
    },
  });

  const InfoRow = ({ label, value }: { label: string; value: string | null | undefined }) => (
    <>
      <Box
        fontSize="sm"
        direction="row"
        justifyContent="space-between"
        childGap="sm"
        padding="xs"
        alignItems="center"
        fontWeight="medium"
        color="grey-700"
      >
        <Box width="40%">{label}:</Box>
        <Box width="60%" textAlign="right">
          {value || 'N/A'}
        </Box>
      </Box>
    </>
  );

  return (
    <Modal ariaLabelledBy="adjustMonthlyPaymentCap" isOpen={isModalOpen} onDismiss={closeModal} maxWidth="500px">
      <Modal.Header id="adjustMonthlyPaymentCap" title="Adjust Monthly Payment Cap" onDismiss={closeModal} />
      <form onSubmit={formik.handleSubmit}>
        <Modal.Body background="grey-50" color="grey-800">
          <Heading size="md">Current Caps</Heading>
          {!currentMonthlyPaymentCap?.override ? (
            <>
              <InfoRow label=">0% Escalation" value={currentMonthlyPaymentCap?.greaterThanZeroEscalationRate} />
              <InfoRow label="0% Escalation" value={currentMonthlyPaymentCap?.zeroEscalationRate} />
              <InfoRow
                label="Org Max >0% Escalation"
                value={currentMonthlyPaymentCap?.orgMaxGreaterThanZeroEscalationRate}
              />
              <InfoRow label="Org Max 0% Escalation" value={currentMonthlyPaymentCap?.orgMaxZeroEscalationRate} />
              <InfoRow label="Cap Table Version" value={currentMonthlyPaymentCap?.capTableVersionUsed} />
              <InfoRow
                label="Caps Applied"
                value={
                  DateTime.fromISO(currentMonthlyPaymentCap?.dateCapsApplied).toUTC().toFormat('MM-dd-yyyy HH:mm') +
                  ' UTC'
                }
              />
            </>
          ) : (
            <>
              <InfoRow
                label="Overridden to Org Max"
                value={
                  DateTime.fromISO(currentMonthlyPaymentCap?.overrideDate).toUTC().toFormat('MM-dd-yyyy HH:mm') +
                  ' UTC'
                }
              />
              <InfoRow label="Overridden to Org Max By" value={currentMonthlyPaymentCap?.overrideUserName} />
            </>
          )}

          {!currentMonthlyPaymentCap?.override && (
            <Box direction="column" margin="lg 0 0 0" childGap="xl">
              <RadioGroup
                title="Adjustment Options"
                id="adjustmentOptions"
                name="adjustmentOptions"
                label="Adjustment Options"
                value={formik.values.adjustmentOption}
                onChange={(event) => formik.setFieldValue('adjustmentOption', event.target.value)}
                options={[
                  {
                    label: 'Update monthly payment cap to current cap table',
                    value: AdjustMonthlyPaymentCapTypes.updatePaymentCaps,
                    id: AdjustMonthlyPaymentCapTypes.updatePaymentCaps,
                  },
                  {
                    label: 'Set monthly payment cap to org maximum',
                    value: AdjustMonthlyPaymentCapTypes.overridePaymentCaps,
                    id: AdjustMonthlyPaymentCapTypes.overridePaymentCaps,
                  },
                ]}
              />
            </Box>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            as="button"
            onClick={() => {
              formik.resetForm();
              closeModal();
            }}
            variant="secondary"
            tone="neutral"
            size="md"
            isLoading={formik.isSubmitting}
          >
            Cancel
          </Button>
          {!currentMonthlyPaymentCap?.override && (
            <Button variant="primary" size="md" type="submit" isLoading={formik.isSubmitting}>
              Save
            </Button>
          )}
        </Modal.Footer>
      </form>
    </Modal>
  );
};
